import React, { useState, useMemo } from 'react';
import { cn } from '../lib/utils';
import IModelData from '../types/ModelData';
import TailwindBarChart, { TailwindBarChartSeries } from './Tailwind/TailwindBarChart';
import TailwindLineChart, { TailwindLineChartSeries } from './Tailwind/TailwindLineChart';
import TailwindPieChart, { TailwindPieChartData } from './Tailwind/TailwindPieChart';
import { useAppStore } from '../shared/stores/useAppStore';
import { round } from '../tools';
import Slider from '@mui/material/Slider';

interface CompanyStatements {
  companyName: string;
  companyId: number;
  statements: {
    [key: string]: IModelData;
  };
}

interface FinancialStatementsTableProps {
  data?: CompanyStatements[];
  className?: string;
}

type TabId = 'p&l' | 'balance_sheet' | 'cash_flow_statement' | 'cash_flow_waterfall' | 'uses_sources';

interface Tab {
  id: TabId;
  label: string;
  type: string;
}

const TABS: Tab[] = [
  { id: 'p&l', label: 'Profit and Loss', type: 'p&l' },
  { id: 'balance_sheet', label: 'Balance Sheet', type: 'balance_sheet' },
  { id: 'cash_flow_statement', label: 'Cash Flow Statement', type: 'cash_flow_statement' },
  { id: 'cash_flow_waterfall', label: 'Cash Flow Waterfall', type: 'cash_flow_waterfall' },
  { id: 'uses_sources', label: 'Uses and Sources', type: 'uses_sources' },
];

// Fields with "total" styling (bold, grey background, top border) per statement type
const TOTAL_FIELDS_BY_TYPE: { [key: string]: string[] } = {
  'p&l': ['ebitda', 'ebit', 'financial_result', 'ebt', 'net_result'],
  'balance_sheet': ['total_assets', 'total_liabilities'],
  'cash_flow_statement': ['operating_cash_flow', 'cash_flow_from_investing', 'cash_flow_from_financing', 'net_cash_flow'],
  'cash_flow_waterfall': ['operating_cash_flow', 'cash_flow_available_for_debt_service', 'cash_flow_available_for_equity_redemption', 'cash_flow_from_financing', 'net_cash_flow'],
  'uses_sources': ['total_uses','total_sources'],
};

// Fields to display in chart per statement type
const CHART_FIELDS: { [key: string]: string[] } = {
  'p&l': ['revenue', 'ebitda', 'ebit', 'net_result'],
  'balance_sheet': ['total_assets', 'total_liabilities', 'shareholders_equity'],
  'cash_flow_statement': ['operating_cash_flow', 'cash_flow_from_investing', 'cash_flow_from_financing', 'net_cash_flow'],
  'cash_flow_waterfall': ['operating_cash_flow', 'cash_flow_available_for_debt_service', 'cash_flow_available_for_equity_redemption', 'net_cash_flow'],
  'uses_sources': [],
};

// Helper to extract dates from field data
const extractDates = (fields: any): string[] => {
  if (!fields || typeof fields !== 'object') return [];

  // Get the first field to extract dates
  const firstField = Object.values(fields)[0] as any;
  if (!firstField?.value?.value?.data) return [];

  const data = firstField.value.value.data;

  // If data is an object with date keys
  if (typeof data === 'object' && !Array.isArray(data)) {
    const dates = Object.keys(data).filter(key => key.match(/^\d{4}-\d{2}-\d{2}$/));
    return dates.sort();
  }

  // If data is an array with date objects
  if (Array.isArray(data) && data.length > 0 && data[0]?.date?.value) {
    return data.map(item => item.date.value).sort();
  }

  return [];
};

// Helper to get value for a specific date from field data
const getValueForDate = (field: any, date: string): any => {
  if (!field?.value?.value?.data) return '-';

  const data = field.value.value.data;

  // If data is an object with date keys
  if (typeof data === 'object' && !Array.isArray(data)) {
    const value = data[date];
    return value !== undefined && value !== null ? value : '-';
  }

  // If data is an array with date objects
  if (Array.isArray(data)) {
    const item = data.find(d => d.date?.value === date);
    return item?.value?.value !== undefined ? item.value.value : '-';
  }

  return '-';
};

// Helper to get a raw numeric value from a field (fallback for non-time-series data)
const getRawFieldValue = (field: any): number => {
  if (!field) return 0;

  // Try field.value.value.data — sum all values if it's an object or array
  const data = field?.value?.value?.data;
  if (data) {
    if (typeof data === 'number') return data;
    if (typeof data === 'object' && !Array.isArray(data)) {
      const values = Object.values(data).filter((v): v is number => typeof v === 'number');
      if (values.length > 0) return values.reduce((a, b) => a + b, 0);
    }
    if (Array.isArray(data)) {
      const nums = data
        .map(d => d?.value?.value ?? d?.value ?? d)
        .filter((v): v is number => typeof v === 'number');
      if (nums.length > 0) return nums.reduce((a, b) => a + b, 0);
    }
  }

  // Try field.value.value directly
  const vv = field?.value?.value;
  if (typeof vv === 'number') return vv;

  // Try field.value directly
  const v = field?.value;
  if (typeof v === 'number') return v;

  return 0;
};

// Helper to format value for display
const formatValue = (value: any, field: any): string => {
  if (value === '-' || value === null || value === undefined) return '-';

  const numValue = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(numValue)) return String(value);

  const isPercentage = field?.is_percentage || field?.suffix === '%';
  const roundable = field?.roundable !== false;
  const precision = useAppStore.getState().userSettings.roundPrecision ?? 2;
  const locale = useAppStore.getState().userSettings.numberFormattingLocale || 'en-GB';

  let formatted: string;
  if (roundable) {
    formatted = round(numValue, precision).toLocaleString(locale, { maximumFractionDigits: precision });
  } else {
    formatted = numValue.toLocaleString(locale);
  }

  if (isPercentage) {
    return `${formatted} %`;
  }

  return formatted;
};

// Helper to format date for display
const formatDate = (dateStr: string): string => {
  if (!dateStr) return '';

  try {
    const date = new Date(dateStr);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${year}`;
  } catch {
    return dateStr;
  }
};

// Helper to format period label
const formatPeriodLabel = (periodKey: string, period: PeriodView): string => {
  switch (period) {
    case 'annual':
      return periodKey; // "2024"
    case 'semi-annual':
      return periodKey; // "2024-S1"
    case 'quarterly':
      return periodKey; // "2024-Q1"
    case 'monthly':
    default:
      return formatDate(periodKey);
  }
};

// Helper to convert snake_case to Title Case
const snakeToTitleCase = (str: string): string => {
  return str
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Helper to get field display name
const getFieldDisplayName = (fieldName: string, field: any): string => {
  const displayName = field?.display_name || field?.name || fieldName;
  // Convert snake_case to Title Case if no display_name is set
  if (!field?.display_name) {
    return snakeToTitleCase(displayName);
  }
  return displayName;
};

// Helper to get unit/suffix
const getUnit = (field: any): string => {
  return field?.suffix || field?.value?.value?.unit?.value || '';
};

// Helper to group dates by period
const groupDatesByPeriod = (dates: string[], period: PeriodView): { [key: string]: string[] } => {
  const groups: { [key: string]: string[] } = {};

  dates.forEach(date => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = d.getMonth(); // 0-11

    let periodKey: string;

    switch (period) {
      case 'annual':
        periodKey = `${year}`;
        break;
      case 'semi-annual':
        const semester = month < 6 ? 'S1' : 'S2';
        periodKey = `${year}-${semester}`;
        break;
      case 'quarterly':
        const quarter = Math.floor(month / 3) + 1;
        periodKey = `${year}-Q${quarter}`;
        break;
      case 'monthly':
      default:
        periodKey = date;
        break;
    }

    if (!groups[periodKey]) {
      groups[periodKey] = [];
    }
    groups[periodKey].push(date);
  });

  return groups;
};

// Helper to aggregate values for a period
const aggregateValues = (dates: string[], field: any, isBalanceSheet: boolean): any => {
  if (dates.length === 0) return '-';

  // For balance sheet, take the last value
  if (isBalanceSheet) {
    const lastDate = dates[dates.length - 1];
    return getValueForDate(field, lastDate);
  }

  // For other statements, sum the values
  let sum = 0;
  let hasValue = false;

  dates.forEach(date => {
    const value = getValueForDate(field, date);
    if (value !== '-' && value !== null && value !== undefined) {
      const numValue = typeof value === 'number' ? value : parseFloat(value);
      if (!isNaN(numValue)) {
        sum += numValue;
        hasValue = true;
      }
    }
  });

  return hasValue ? sum : '-';
};

type PeriodView = 'monthly' | 'quarterly' | 'semi-annual' | 'annual';
type ChartType = 'bar' | 'line';

export default function FinancialStatementsTable({ data, className }: FinancialStatementsTableProps) {
  const [activeTab, setActiveTab] = useState<TabId>('p&l');
  const [selectedCompanyIndex, setSelectedCompanyIndex] = useState<number>(0);
  const [periodView, setPeriodView] = useState<PeriodView>('monthly');
  const [chartType, setChartType] = useState<ChartType>('bar');
  const [chartStartIndex, setChartStartIndex] = useState<number>(0);
  const [chartEndIndex, setChartEndIndex] = useState<number | null>(null);
  const [piePeriodIndex, setPiePeriodIndex] = useState<number>(0);

  // Get the statement data for the active tab
  const statementData = useMemo(() => {
    if (!data || data.length === 0) return null;

    const selectedCompany = data[selectedCompanyIndex] || data[0];
    return selectedCompany?.statements[activeTab] || null;
  }, [data, activeTab, selectedCompanyIndex]);

  // Extract dates and fields
  const rawDates = useMemo(() => {
    if (!statementData?.fields) return [];
    return extractDates(statementData.fields);
  }, [statementData]);

  // Group dates by period and get period keys
  const { periodKeys, dateGroups } = useMemo(() => {
    if (rawDates.length === 0) return { periodKeys: [], dateGroups: {} };

    const groups = groupDatesByPeriod(rawDates, periodView);
    const keys = Object.keys(groups).sort();

    return { periodKeys: keys, dateGroups: groups };
  }, [rawDates, periodView]);

  const fields = useMemo(() => {
    if (!statementData?.fields) return [];
    return Object.entries(statementData.fields).filter(([_, field]: any) => !field.hide).map(([name, field]) => ({
      name,
      field,
      displayName: getFieldDisplayName(name, field),
      unit: getUnit(field),
    }));
  }, [statementData]);

  // Check if current tab is balance sheet
  const isBalanceSheet = activeTab === 'balance_sheet';

  // Build chart data from key fields
  const { chartData, chartSeries } = useMemo(() => {
    if (!statementData?.fields || periodKeys.length === 0) {
      return { chartData: [], chartSeries: [] };
    }

    // Get the chart field names for the active tab, fallback to total fields for this type
    const targetFields = CHART_FIELDS[activeTab]?.length > 0
      ? CHART_FIELDS[activeTab]
      : (TOTAL_FIELDS_BY_TYPE[activeTab] || []);

    // Filter to fields that actually exist in the data and are not percentages
    const chartFields = fields.filter(f =>
      targetFields.includes(f.name) && !(f.field as any)?.is_percentage && (f.field as any)?.suffix !== '%'
    );

    if (chartFields.length === 0) return { chartData: [], chartSeries: [] };

    // Build data array: one entry per period
    const data = periodKeys.map(periodKey => {
      const entry: any = { period: formatPeriodLabel(periodKey, periodView) };
      const datesInPeriod = dateGroups[periodKey] || [];

      chartFields.forEach(({ name, field }) => {
        const value = aggregateValues(datesInPeriod, field, isBalanceSheet);
        entry[name] = value !== '-' ? (typeof value === 'number' ? value : parseFloat(value)) : 0;
      });

      return entry;
    });

    // Build series config
    const series: TailwindBarChartSeries[] = chartFields.map(({ name, displayName }) => ({
      dataKey: name,
      label: displayName,
    }));

    return { chartData: data, chartSeries: series };
  }, [statementData, fields, periodKeys, dateGroups, periodView, activeTab, isBalanceSheet]);

  // Reset chart range and pie period when period keys change
  React.useEffect(() => {
    setChartStartIndex(0);
    setChartEndIndex(null);
    setPiePeriodIndex(0);
  }, [periodKeys.length, activeTab, periodView]);

  // Effective end index (default to last period)
  const effectiveEndIndex = chartEndIndex ?? (periodKeys.length - 1);

  // Filtered chart data based on selected range
  const filteredChartData = useMemo(() => {
    if (chartData.length === 0) return [];
    const start = Math.max(0, chartStartIndex);
    const end = Math.min(chartData.length - 1, effectiveEndIndex);
    return chartData.slice(start, end + 1);
  }, [chartData, chartStartIndex, effectiveEndIndex]);

  // Period labels for the range selectors
  const periodLabels = useMemo(() => {
    return periodKeys.map(key => formatPeriodLabel(key, periodView));
  }, [periodKeys, periodView]);

  // Build pie chart data for uses_sources (split into Uses and Sources)
  const { usesData, sourcesData, hasUsesFields, hasSourcesFields } = useMemo(() => {
    if (activeTab !== 'uses_sources' || !statementData?.fields) {
      return { usesData: [], sourcesData: [], hasUsesFields: false, hasSourcesFields: false };
    }

    const fieldNames = fields.map(f => f.name);
    const totalUsesIdx = fieldNames.indexOf('total_uses');
    const totalSourcesIdx = fieldNames.indexOf('total_sources');

    // Uses: fields before total_uses
    const usesFieldNames = totalUsesIdx > 0 ? fieldNames.slice(0, totalUsesIdx) : [];
    // Sources: fields between total_uses and total_sources
    const sourcesFieldNames = totalUsesIdx >= 0 && totalSourcesIdx > totalUsesIdx
      ? fieldNames.slice(totalUsesIdx + 1, totalSourcesIdx)
      : [];

    const buildPieData = (allowedNames: string[]): TailwindPieChartData[] => {
      const filtered = fields.filter(f => {
        const fa = f.field as any;
        return allowedNames.includes(f.name) && !fa?.is_percentage && fa?.suffix !== '%';
      });

      if (periodKeys.length > 0) {
        const safePieIndex = Math.min(piePeriodIndex, periodKeys.length - 1);
        const periodKey = periodKeys[safePieIndex];
        const datesInPeriod = dateGroups[periodKey] || [];

        return filtered
          .map(({ field, displayName }) => {
            const raw = aggregateValues(datesInPeriod, field, false);
            const num = raw !== '-' ? (typeof raw === 'number' ? raw : parseFloat(raw)) : 0;
            return { name: displayName, value: Math.abs(num) };
          })
          .filter(d => d.value > 0);
      }

      return filtered
        .map(({ field, displayName }) => {
          const num = getRawFieldValue(field);
          return { name: displayName, value: Math.abs(num) };
        })
        .filter(d => d.value > 0);
    };

    return {
      usesData: buildPieData(usesFieldNames),
      sourcesData: buildPieData(sourcesFieldNames),
      hasUsesFields: usesFieldNames.length > 0,
      hasSourcesFields: sourcesFieldNames.length > 0,
    };
  }, [activeTab, statementData, fields, periodKeys, dateGroups, piePeriodIndex]);

  // Pie chart custom tooltip factory
  const makePieTooltip = (dataset: TailwindPieChartData[]) => ({ active, payload }: any) => {
    if (!active || !payload || payload.length === 0) return null;
    const precision = useAppStore.getState().userSettings.roundPrecision ?? 2;
    const locale = useAppStore.getState().userSettings.numberFormattingLocale || 'en-GB';
    const entry = payload[0];
    const total = dataset.reduce((sum, d) => sum + d.value, 0);
    const pct = total > 0 ? ((entry.value / total) * 100).toFixed(1) : '0';
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3">
        <p className="text-sm font-semibold text-slate-800 mb-1">{entry.name}</p>
        <p className="text-xs text-slate-600">
          {round(entry.value, precision).toLocaleString(locale, { maximumFractionDigits: precision })}
          <span className="text-slate-400 ml-2">({pct}%)</span>
        </p>
      </div>
    );
  };

  // Chart number formatter (Y-axis ticks)
  const chartTickFormatter = (value: any) => {
    if (value === null || value === undefined) return '';
    const num = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(num)) return String(value);
    const locale = useAppStore.getState().userSettings.numberFormattingLocale || 'en-GB';
    return num.toLocaleString(locale, { maximumFractionDigits: 0 });
  };

  // Chart custom tooltip
  const ChartTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || payload.length === 0) return null;
    const precision = useAppStore.getState().userSettings.roundPrecision ?? 2;
    const locale = useAppStore.getState().userSettings.numberFormattingLocale || 'en-GB';
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3">
        <p className="text-sm font-semibold text-slate-800 mb-1">{label}</p>
        {payload.map((entry: any, i: number) => (
          <p key={i} className="text-xs text-slate-600 flex items-center gap-2">
            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
            <span>{entry.name}:</span>
            <span className="font-medium text-slate-800">
              {round(entry.value, precision).toLocaleString(locale, { maximumFractionDigits: precision })}
            </span>
          </p>
        ))}
      </div>
    );
  };

  // Render table
  const renderTable = () => {
    if (!statementData || fields.length === 0) {
      return (
        <div className="text-center text-slate-500 py-8">
          No data available for this statement
        </div>
      );
    }

    return (
      <div className="overflow-auto relative z-0">
        <table className="w-full border-collapse text-sm">
          <thead className="bg-slate-50 sticky top-0 z-20">
            <tr className="border-b border-slate-200">
              <th className="p-2 text-xs font-medium text-slate-500 text-left sticky left-0 z-30 bg-slate-50 min-w-[160px]">
                Item
              </th>
              {periodKeys.map((periodKey) => (
                <th key={periodKey} className="p-2 text-xs font-medium text-slate-500 text-center min-w-[100px]">
                  {formatPeriodLabel(periodKey, periodView)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {fields.map(({ name, field, displayName, unit }) => {
              // Check if this field name should have total styling
              const isTotal = (TOTAL_FIELDS_BY_TYPE[activeTab] || []).includes(name);

              return (
                <tr
                  key={name}
                  className={cn(
                    'border-b border-slate-200',
                    isTotal
                      ? 'bg-[#fafafa] border-t-2 border-t-slate-300'
                      : 'hover:bg-slate-50'
                  )}
                >
                  <td className={cn(
                    "p-2 text-left sticky left-0 z-10 border-r border-slate-100 whitespace-nowrap",
                    isTotal ? "font-semibold text-slate-700 bg-[#fafafa]" : "font-semibold text-slate-700 bg-white"
                  )}>
                    {displayName}
                  </td>
                  {periodKeys.map((periodKey) => {
                    const datesInPeriod = dateGroups[periodKey] || [];
                    const value = aggregateValues(datesInPeriod, field, isBalanceSheet);
                    const formattedValue = formatValue(value, field);
                    return (
                      <td
                        key={periodKey}
                        className={cn(
                          "p-2 text-center tabular-nums whitespace-nowrap",
                          isTotal ? "bg-[#fafafa]" : "bg-white"
                        )}
                      >
                        {formattedValue}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Show message if no data
  if (!data || data.length === 0) {
    return (
      <div className={cn('w-full text-center text-slate-500 py-8', className)}>
        No financial statements data available
      </div>
    );
  }

  // Get selected company
  const selectedCompany = data[selectedCompanyIndex] || data[0];
  const companyName = selectedCompany?.companyName || 'Company';

  return (
    <div className={cn('w-full', className)}>
      {/* Company Selector */}
      {data.length > 0 && (
        <div className="mb-4">
          {data.length === 1 ? (
            <h3 className="text-lg font-semibold text-slate-900">{companyName}</h3>
          ) : (
            <div className="flex items-center gap-3">
              <label htmlFor="company-selector" className="text-sm font-medium text-slate-700">
                Company:
              </label>
              <select
                id="company-selector"
                value={selectedCompanyIndex}
                onChange={(e) => setSelectedCompanyIndex(parseInt(e.target.value))}
                className="pl-3 pr-8 py-2 border border-slate-300 rounded-md text-sm max-w-[300px] truncate focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              >
                {data.map((company, index) => (
                  <option key={company.companyId} value={index}>
                    {company.companyName}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Period View Toggle */}
      <div className="mb-4 flex items-center gap-2">
        <span className="text-sm font-medium text-slate-700">View:</span>
        <div className="inline-flex rounded-lg border border-slate-300 p-1">
          <button
            onClick={() => setPeriodView('monthly')}
            className={cn(
              'px-3 py-1 text-xs font-medium rounded-md transition-colors',
              periodView === 'monthly'
                ? 'bg-primary text-white'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            )}
          >
            Monthly
          </button>
          <button
            onClick={() => setPeriodView('quarterly')}
            className={cn(
              'px-3 py-1 text-xs font-medium rounded-md transition-colors',
              periodView === 'quarterly'
                ? 'bg-primary text-white'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            )}
          >
            Quarterly
          </button>
          <button
            onClick={() => setPeriodView('semi-annual')}
            className={cn(
              'px-3 py-1 text-xs font-medium rounded-md transition-colors',
              periodView === 'semi-annual'
                ? 'bg-primary text-white'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            )}
          >
            Semi-Annual
          </button>
          <button
            onClick={() => setPeriodView('annual')}
            className={cn(
              'px-3 py-1 text-xs font-medium rounded-md transition-colors',
              periodView === 'annual'
                ? 'bg-primary text-white'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            )}
          >
            Annual
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-slate-200">
        <nav className="flex flex-wrap -mb-px" role="tablist">
          {TABS.map((tab) => {
            const hasData = selectedCompany?.statements[tab.type];
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                role="tab"
                aria-selected={activeTab === tab.id}
                aria-controls={tab.id}
                disabled={!hasData}
                className={cn(
                  'px-4 py-3 text-sm font-medium transition-colors border-b-2',
                  activeTab === tab.id
                    ? 'border-primary text-primary bg-primary/5'
                    : hasData
                    ? 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300'
                    : 'border-transparent text-slate-400 cursor-not-allowed opacity-50'
                )}
              >
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-4">
        {renderTable()}
      </div>

      {/* Pie Charts for Uses & Sources */}
      {activeTab === 'uses_sources' && (hasUsesFields || hasSourcesFields) && (
        <div className="mt-8">
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <h4 className="text-sm font-semibold text-slate-700">Distribution</h4>
            {periodLabels.length > 1 && (
              <select
                value={piePeriodIndex}
                onChange={(e) => setPiePeriodIndex(parseInt(e.target.value))}
                className="px-2 py-1 border border-slate-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              >
                {periodLabels.map((label, i) => (
                  <option key={i} value={i}>{label}</option>
                ))}
              </select>
            )}
          </div>
          <div className="grid grid-cols-2 gap-8">
            {hasUsesFields && (
              <div>
                <h5 className="text-sm font-semibold text-slate-600 text-center mb-2">Uses</h5>
                {usesData.length > 0 ? (
                  <TailwindPieChart
                    data={usesData}
                    height={350}
                    innerRadius={60}
                    outerRadius={120}
                    showLegend
                    showTooltip
                    showLabels
                    customTooltip={makePieTooltip(usesData)}
                    centerLabel="Uses"
                  />
                ) : (
                  <div className="flex items-center justify-center h-[350px] text-slate-500">
                    <div className="text-center">
                      <span className="material-symbols-outlined text-[48px] text-slate-300 mb-2 block">
                        pie_chart
                      </span>
                      <p>No data to display</p>
                    </div>
                  </div>
                )}
              </div>
            )}
            {hasSourcesFields && (
              <div>
                <h5 className="text-sm font-semibold text-slate-600 text-center mb-2">Sources</h5>
                {sourcesData.length > 0 ? (
                  <TailwindPieChart
                    data={sourcesData}
                    height={350}
                    innerRadius={60}
                    outerRadius={120}
                    showLegend
                    showTooltip
                    showLabels
                    customTooltip={makePieTooltip(sourcesData)}
                    centerLabel="Sources"
                  />
                ) : (
                  <div className="flex items-center justify-center h-[350px] text-slate-500">
                    <div className="text-center">
                      <span className="material-symbols-outlined text-[48px] text-slate-300 mb-2 block">
                        pie_chart
                      </span>
                      <p>No data to display</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bar / Line Chart */}
      {chartData.length > 0 && chartSeries.length > 0 && (
        <div className="mt-8">
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <h4 className="text-sm font-semibold text-slate-700">Key Metrics</h4>

            {/* Chart Type Toggle */}
            <div className="inline-flex rounded-lg border border-slate-300 p-1">
              <button
                onClick={() => setChartType('bar')}
                className={cn(
                  'px-3 py-1 text-xs font-medium rounded-md transition-colors',
                  chartType === 'bar'
                    ? 'bg-primary text-white'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                )}
              >
                Bar
              </button>
              <button
                onClick={() => setChartType('line')}
                className={cn(
                  'px-3 py-1 text-xs font-medium rounded-md transition-colors',
                  chartType === 'line'
                    ? 'bg-primary text-white'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                )}
              >
                Line
              </button>
            </div>
          </div>

          {chartType === 'bar' ? (
            <TailwindBarChart
              data={filteredChartData}
              series={chartSeries as TailwindBarChartSeries[]}
              xAxisKey="period"
              height={300}
              showGrid
              showLegend
              showTooltip
              customTooltip={ChartTooltip}
              yAxisTickFormatter={chartTickFormatter}
            />
          ) : (
            <TailwindLineChart
              data={filteredChartData}
              series={chartSeries as TailwindLineChartSeries[]}
              xAxisKey="period"
              height={300}
              showGrid
              showLegend
              showTooltip
              customTooltip={ChartTooltip}
              yAxisTickFormatter={chartTickFormatter}
            />
          )}

          {/* Period Range Slider */}
          {periodLabels.length > 2 && (
            <div className="px-4 mt-2">
              <Slider
                value={[chartStartIndex, effectiveEndIndex]}
                onChange={(_e, newValue) => {
                  const [start, end] = newValue as number[];
                  setChartStartIndex(start);
                  setChartEndIndex(end);
                }}
                min={0}
                max={periodLabels.length - 1}
                step={1}
                marks={periodLabels.map((label, i) => ({
                  value: i,
                  label: periodLabels.length <= 12 ? label : (i === 0 || i === periodLabels.length - 1 ? label : undefined),
                }))}
                valueLabelDisplay="auto"
                valueLabelFormat={(val) => periodLabels[val] || ''}
                sx={{
                  color: '#135bec',
                  '& .MuiSlider-markLabel': {
                    fontSize: '0.65rem',
                    color: '#64748b',
                  },
                  '& .MuiSlider-valueLabel': {
                    backgroundColor: '#1e293b',
                    fontSize: '0.7rem',
                    borderRadius: '4px',
                  },
                  '& .MuiSlider-thumb': {
                    width: 16,
                    height: 16,
                  },
                  '& .MuiSlider-rail': {
                    backgroundColor: '#e2e8f0',
                  },
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
